import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, GripVertical, Pencil, Trash2, Check, X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Row = ({ moment, index, onRename, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: moment.id });
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(moment.name);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    boxShadow: isDragging ? "0 16px 40px rgba(20,19,22,0.16)" : undefined,
  };
  const save = () => {
    if (name.trim()) onRename(moment.id, name.trim());
    setEditing(false);
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`cortejo-row-${moment.id}`}
      className="flex items-center gap-3 rounded-xl border border-[color:var(--brand-border)] bg-white p-3"
    >
      <button {...attributes} {...listeners} data-testid={`cortejo-drag-${moment.id}`} className="cursor-grab touch-none text-[color:var(--brand-muted)] active:cursor-grabbing">
        <GripVertical className="h-5 w-5" />
      </button>
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[color:var(--brand-paper)] text-xs font-semibold text-[color:var(--brand-muted)]">{index + 1}</span>
      {editing ? (
        <div className="flex flex-1 items-center gap-2">
          <Input data-testid={`cortejo-name-input-${moment.id}`} value={name} autoFocus onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="h-9 flex-1" />
          <Button size="sm" onClick={save} className="h-9 bg-[color:var(--brand-marsala)] text-white"><Check className="h-4 w-4" /></Button>
        </div>
      ) : (
        <>
          <span className="flex-1 font-serif text-base font-medium text-[color:var(--brand-graphite)]">{moment.name}</span>
          <button data-testid={`cortejo-rename-${moment.id}`} onClick={() => setEditing(true)} className="rounded-lg p-1.5 text-[color:var(--brand-muted)] hover:bg-[color:var(--brand-paper)]"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`cortejo-delete-${moment.id}`} onClick={() => onDelete(moment.id)} className="rounded-lg p-1.5 text-[color:var(--brand-danger)] hover:bg-[color:var(--brand-paper)]"><Trash2 className="h-4 w-4" /></button>
        </>
      )}
    </div>
  );
};

export const CortejoStep = ({ moments, onMomentsChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = moments.findIndex((m) => m.id === active.id);
    const newIndex = moments.findIndex((m) => m.id === over.id);
    onMomentsChange(arrayMove(moments, oldIndex, newIndex).map((m, i) => ({ ...m, order: i })));
  };

  const rename = (id, name) => onMomentsChange(moments.map((m) => (m.id === id ? { ...m, name } : m)));
  const del = (id) => onMomentsChange(moments.filter((m) => m.id !== id).map((m, i) => ({ ...m, order: i })));
  const add = () => {
    const id = (crypto.randomUUID && crypto.randomUUID()) || `m-${Date.now()}`;
    onMomentsChange([...moments, {
      id, order: moments.length, name: "Novo momento", music: null, manual_link: "",
      execution_type: null, execution_detail: "", surprise: { is: false, for_whom: "" },
      undecided: false, not_applicable: false, observations: "",
    }]);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mx-auto max-w-2xl">
      <h2 className="font-serif text-2xl font-semibold text-[color:var(--brand-graphite)] sm:text-3xl">Ordem do cortejo</h2>
      <p className="mt-2 text-sm text-[color:var(--brand-muted)]">
        Monte a sequência dos momentos da sua cerimônia. As músicas você escolhe na próxima etapa.
      </p>
      <div className="mt-3 flex items-start gap-2 rounded-xl border border-[color:var(--brand-champagne)] bg-[color:var(--brand-champagne-soft)] p-3 text-sm text-[color:var(--brand-graphite)]">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-marsala)]" />
        <span>
          <strong>* Como preencher:</strong> deixe apenas os momentos que existirão na sua cerimônia.
          Remova o que não terá (ex.: "Entrada dos Padrinhos"), renomeie no lápis e
          <strong> arraste pela alça</strong> para reorganizar a ordem. Pode adicionar momentos como "Entrada dos Avós".
        </span>
      </div>

      <div className="mt-5 space-y-2">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={moments.map((m) => m.id)} strategy={verticalListSortingStrategy}>
            {moments.map((m, i) => (
              <Row key={m.id} moment={m} index={i} onRename={rename} onDelete={del} />
            ))}
          </SortableContext>
        </DndContext>
        <Button data-testid="add-moment-button" variant="outline" onClick={add} className="h-12 w-full border-dashed border-[color:var(--brand-border)] text-[color:var(--brand-graphite)] hover:bg-[color:var(--brand-paper)]">
          <Plus className="mr-2 h-4 w-4" /> Adicionar momento
        </Button>
      </div>
    </motion.div>
  );
};
